import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { useFormContext, useWatch } from "react-hook-form";

import isEqual from "lodash/isEqual";
import { WarningConfirmationModal } from "../../../../../../../../components/modal/WarningModal";
import { commonSiteCoreKeys } from "../../../../../../../../helpers/constants/siteCoreKeys";
import { UserDetails } from "../../../../../../../../helpers/types/common";
import {
  getFullName,
  getValueOrBlank,
  isEmpty,
} from "../../../../../../../../helpers/utils";
import { useSiteCore } from "../../../../../../../../hooks/useSiteCore";
import {
  SalesCreditDetailsFormKeys,
  SalesCreditTableData,
} from "../../../../../../../createProgram/modelsAndSchemas/Models";
import { salesCreditSiteCoreKeys } from "../../../../../../siteCore/SiteCoreKeys";
import { SalesCreditDetailsWrapper } from "./SalesCreditDetails.style";
import { SalesCreditDetailsTable } from "./salesCreditDetailsTable/SalesCreditDetailsTable";

export interface SalesCreditDetailsProps {
  pageId: string;
  isInViewMode?: boolean;
  resultsFormKey: string;
  clientRelationshipOwnerList: UserDetails[];
  isEditProgram?: boolean;
}

export const SalesCreditDetails = forwardRef(
  (props: SalesCreditDetailsProps, ref) => {
    const {
      pageId,
      isInViewMode = false,
      resultsFormKey,
      isEditProgram = false,
      clientRelationshipOwnerList,
    } = props;
    const { getValues, setValue, setError } = useFormContext();
    const { fetchValidationMessage, commonLabels, fetchLabel } = useSiteCore();
    const selectedDeleteSalesCreditRef = useRef<SalesCreditTableData>();

    const [filteredResults, setFilteredResults] = useState<
      SalesCreditTableData[]
    >([]);

    const [onDataAddedCallback, setOnDataAddedCallback] =
      useState<boolean>(false);
    const [onDataDeletedCallback, setOnDataDeletedCallback] =
      useState<boolean>(false);
    const [showDeleteSalesCreditModal, setShowDeleteSalesCreditModal] =
      useState(false);

    const [showDefaultLCROEntry, setShowDefaultLCROEntry] =
      useState<boolean>(!isEditProgram);

    const clientRelationshipOwnerKey =
      SalesCreditDetailsFormKeys.clientRelationshipOwner;
    const percentageKey = SalesCreditDetailsFormKeys.percentage;
    const officeKey = SalesCreditDetailsFormKeys.officeName;

    useImperativeHandle(ref, () => ({
      resetData,
    }));

    const resetData = () => {
      clearAllValues();
    };

    const clearAllValues = () => {
      setFilteredResults([]);
      clearInputValues();
    };

    const watchSalesCreditEntriesKey = useWatch({
      name: resultsFormKey,
    });

    const handleSalesCreditDeleteClick = (row: SalesCreditTableData) => {
      const filteredResultsCopy = [...filteredResults];
      const index = filteredResultsCopy.findIndex((item) => {
        return item.clientRelationshipOwner === row.clientRelationshipOwner;
      });
      filteredResultsCopy.splice(index, 1);
      setShowDefaultLCROEntry(false);
      sortFilterData(filteredResultsCopy);
      setFilteredResults(filteredResultsCopy);
      addEntriesToFormKey(filteredResultsCopy, true);
      setOnDataDeletedCallback(!onDataDeletedCallback);
    };

    const sortFilterData = (filteredResults: SalesCreditTableData[]) => {
      filteredResults.sort((a, b) => {
        return getValueOrBlank(a?.clientRelationshipOwner)?.localeCompare(
          getValueOrBlank(b?.clientRelationshipOwner),
        );
      });
    };
    useEffect(() => {
      const salesCreditEntriesKeyValue = watchSalesCreditEntriesKey;
      if (isEmpty(salesCreditEntriesKeyValue)) {
        setFilteredResults([]);
        return;
      }

      if (!isEqual(filteredResults, salesCreditEntriesKeyValue)) {
        const filteredResultsCopy: SalesCreditTableData[] =
          salesCreditEntriesKeyValue.map((item: SalesCreditTableData) => {
            const firstName = getValueOrBlank(item?.firstName);
            const lastName = getValueOrBlank(item?.lastName);
            return {
              id: item.id,
              localClientRelationshipOwnerId:
                item?.localClientRelationshipOwnerId,
              clientRelationshipOwner: getFullName(firstName, lastName),
              firstName,
              lastName,
              email: getValueOrBlank(item?.email),
              percentage: item?.percentage,
              officeName: item?.officeName,
            };
          });
        setFilteredResults(filteredResultsCopy);
        addEntriesToFormKey(filteredResultsCopy);
      }
    }, [watchSalesCreditEntriesKey]);

    const addEntriesToFormKey = (
      results: SalesCreditTableData[],
      isEntryDeleted = false,
    ) => {
      setValue(resultsFormKey, results, {
        shouldDirty: isEntryDeleted,
      });
    };

    const checkValidations = () => validateSalesCreditEntry();

    const validateSalesCreditEntry = (): SalesCreditTableData | null => {
      const clientRelationshipOwner = getValues(clientRelationshipOwnerKey);
      const percentageValue = getValues(percentageKey);

      validateField(clientRelationshipOwner, clientRelationshipOwnerKey);
      validateField(percentageValue, percentageKey);

      if (isEmpty(clientRelationshipOwner) || isEmpty(percentageValue)) {
        return null;
      }
      const getSalesCreditData = clientRelationshipOwnerList?.find(
        (salesCreditObj: UserDetails) =>
          salesCreditObj.id === clientRelationshipOwner,
      );

      const firstName = getValueOrBlank(getSalesCreditData?.firstName);
      const lastName = getValueOrBlank(getSalesCreditData?.lastName);

      return {
        localClientRelationshipOwnerId: getValueOrBlank(getSalesCreditData?.id),
        clientRelationshipOwner: getFullName(firstName, lastName),
        firstName,
        lastName,
        email: getValueOrBlank(getSalesCreditData?.email),
        percentage: percentageValue,
        officeName: getValueOrBlank(getSalesCreditData?.officeName),
      };
    };

    const validateField = (fieldValue: string | number, fieldKey: string) => {
      if (isEmpty(fieldValue)) {
        setError(fieldKey, {
          type: "custom",
          message: fetchValidationMessage(
            commonSiteCoreKeys.requiredValidation,
          ),
        });
      }
    };

    const addSalesCredit = () => {
      let newSalesCreditEntry: SalesCreditTableData | null = null;
      newSalesCreditEntry = checkValidations();

      if (newSalesCreditEntry) {
        clearInputValues();
        const filteredResultsCopy = [...filteredResults, newSalesCreditEntry];
        sortFilterData(filteredResultsCopy);
        setFilteredResults(filteredResultsCopy);
        addEntriesToFormKey(filteredResultsCopy);
        setOnDataAddedCallback(!onDataAddedCallback);
      }
    };

    const clearInputValues = () => {
      setValue(clientRelationshipOwnerKey, "");
      setValue(percentageKey, "");
      setValue(officeKey, "");
    };

    const handleModalClose = () => {
      if (showDeleteSalesCreditModal) {
        setShowDeleteSalesCreditModal(false);
      }
    };

    const handleDeleteSalesCreditClick = () => {
      const selectedSalesCredit = selectedDeleteSalesCreditRef.current;
      if (selectedSalesCredit) {
        setShowDeleteSalesCreditModal(false);
        handleSalesCreditDeleteClick(selectedSalesCredit);
      }
    };

    const deleteSalesCreditAction = (row: SalesCreditTableData) => {
      if (!showDeleteSalesCreditModal) {
        selectedDeleteSalesCreditRef.current = row;
        setShowDeleteSalesCreditModal(true);
      }
    };

    const handleSalesCreditAction = (row: SalesCreditTableData) => {
      deleteSalesCreditAction(row);
    };

    return (
      <SalesCreditDetailsWrapper>
        <SalesCreditDetailsTable
          filteredResults={filteredResults}
          addSalesCredit={addSalesCredit}
          onDeleteSalesCreditEntryAction={handleSalesCreditAction}
          onSalesCreditAddedCallback={onDataAddedCallback}
          clientRelationshipOwnerList={clientRelationshipOwnerList ?? []}
          onSalesCreditDeletedCallback={onDataDeletedCallback}
          pageId={pageId}
          isInViewMode={isInViewMode}
          showDefaultEntry={showDefaultLCROEntry}
        />
        {showDeleteSalesCreditModal && (
          <WarningConfirmationModal
            show={showDeleteSalesCreditModal}
            modalTitle={commonLabels.confirmation}
            secondaryButtonTitle={commonLabels.no}
            onClose={handleModalClose}
            onPrimaryButtonClick={handleDeleteSalesCreditClick}
            labelContent={fetchLabel(
              salesCreditSiteCoreKeys.salesCredit,
              salesCreditSiteCoreKeys.areYouSureYouWantToDeleteThisClientRelationshipOwner,
            )}
          />
        )}
      </SalesCreditDetailsWrapper>
    );
  },
);
